import { useCallback, useEffect, useMemo, useState } from "react";
import { captureError } from "@/lib/sentry";
import AppLayout from "@/components/AppLayout";
import { api, LEAGUES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircleIcon } from "@phosphor-icons/react";

export default function Teams() {
  const { user, setUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [league, setLeague] = useState("NBA");
  const [favorites, setFavorites] = useState(user?.favorite_teams || []);
  const [saving, setSaving] = useState(false);

  const loadTeams = useCallback(async () => {
    try {
      const r = await api.get("/teams");
      setTeams(r.data || []);
    } catch (err) {
      captureError("failed to load teams:", err);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    setFavorites(user?.favorite_teams || []);
  }, [user]);

  const toggle = useCallback((abbr, lg) => {
    const key = `${lg}:${abbr}`;
    setFavorites((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/preferences", { favorite_teams: favorites });
      setUser(res.data);
    } catch (err) {
      captureError("failed to save teams:", err);
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => teams.filter((t) => t.league === league), [teams, league]);

  return (
    <AppLayout>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">My Teams</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Ride or Die</h1>
        <p className="text-white/60 text-sm mt-2">Pick the teams that shape your feed and storylines.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {LEAGUES.filter((l) => l !== "ALL").map((lg) => (
          <button
            key={lg}
            onClick={() => setLeague(lg)}
            data-testid={`teams-tab-${lg}`}
            className={`px-4 py-2 text-xs font-black uppercase tracking-[0.15em] rounded-md border transition-colors ${
              league === lg ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]" : "border-white/10 text-white/60 hover:text-white"
            }`}
          >
            {lg}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        {filtered.map((t) => {
          const key = `${t.league}:${t.abbr}`;
          const active = favorites.includes(key);
          return (
            <button
              key={key}
              data-testid={`team-${t.abbr}-${t.league}`}
              onClick={() => toggle(t.abbr, t.league)}
              className={`p-4 rounded-md text-left transition-colors border ${
                active ? "border-[#32D74B] bg-[#32D74B]/5" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="w-10 h-10 rounded flex items-center justify-center font-display font-bold text-sm tracking-wide"
                  style={{ backgroundColor: `${t.color}22`, color: t.color, border: `1px solid ${t.color}55` }}
                >
                  {t.abbr}
                </div>
                {active && <CheckCircleIcon weight="fill" size={18} className="text-[#32D74B]" />}
              </div>
              <p className="font-display font-bold text-sm uppercase tracking-tight">{t.name}</p>
            </button>
          );
        })}
      </div>
      <button
        data-testid="save-teams-btn"
        onClick={save}
        disabled={saving}
        className="px-6 py-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-bold uppercase tracking-wider text-sm"
      >
        {saving ? "Saving…" : "Save my teams"}
      </button>
    </AppLayout>
  );
}
